import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { Button } from '../../../../components/ui/button';
import { CancellationInfo } from '../../../../services/cancellationServices';

interface CancellationConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onProceed: () => void;
  cancellationInfo: CancellationInfo | null;
}

export const CancellationConfirmModal: React.FC<
  CancellationConfirmModalProps
> = ({ open, onClose, onProceed, cancellationInfo }) => {
  const { t } = useTranslation();

  if (!cancellationInfo) return null;

  const periodEndDate = new Date(
    cancellationInfo.currentPeriodEnd,
  ).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Build features list from raw data with i18n
  const features: string[] = [];
  const isAnnual = cancellationInfo.packageFeatures.term === 'annual';

  // Add recording limit
  if (cancellationInfo.packageFeatures.recordingRequestsCount === -1) {
    features.push(
      t(
        'settings.billing.cancellation.features.unlimitedRecordings',
        'Unlimited recordings',
      ),
    );
  } else {
    const key = isAnnual
      ? 'settings.billing.cancellation.features.limitedRecordingsAnnual'
      : 'settings.billing.cancellation.features.limitedRecordings';
    const fallback = isAnnual
      ? '{{count}} recordings per year'
      : '{{count}} recordings per month';
    features.push(
      t(key, fallback, {
        count: cancellationInfo.packageFeatures.recordingRequestsCount,
      }),
    );
  }

  // Add transcription limit
  if (cancellationInfo.packageFeatures.transcriptionCount === -1) {
    features.push(
      t(
        'settings.billing.cancellation.features.unlimitedTranscriptions',
        'Unlimited transcriptions',
      ),
    );
  } else {
    const key = isAnnual
      ? 'settings.billing.cancellation.features.limitedTranscriptionsAnnual'
      : 'settings.billing.cancellation.features.limitedTranscriptions';
    const fallback = isAnnual
      ? '{{count}} transcriptions per year'
      : '{{count}} transcriptions per month';
    features.push(
      t(key, fallback, {
        count: cancellationInfo.packageFeatures.transcriptionCount,
      }),
    );
  }

  // Add paid plan features if applicable
  if (cancellationInfo.packageFeatures.isPaid) {
    features.push(
      t(
        'settings.billing.cancellation.features.aiSummaries',
        'AI-powered summaries and insights',
      ),
      t(
        'settings.billing.cancellation.features.advancedEditing',
        'Advanced editing tools',
      ),
      t(
        'settings.billing.cancellation.features.prioritySupport',
        'Priority support',
      ),
      t(
        'settings.billing.cancellation.features.cloudStorage',
        'Cloud storage and sync',
      ),
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {t(
              'settings.billing.cancellation.confirm.title',
              'Are you sure you want to cancel?',
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              'settings.billing.cancellation.confirm.description',
              'Your access to these features will be lost at the end of the billing period.',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start gap-2">
                <svg
                  className="h-4 w-4 mt-0.5 text-muted-foreground"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>

          <p className="text-sm text-muted-foreground">
            {t(
              'settings.billing.cancellation.confirm.remainsActiveUntil',
              'Your subscription will remain active until {{date}}',
              { date: periodEndDate },
            )}
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onProceed}>
            {t(
              'settings.billing.cancellation.confirm.cancelPlan',
              'Cancel plan',
            )}
          </Button>
          <Button onClick={onClose}>
            {t('settings.billing.cancellation.confirm.keepPlan', 'Keep plan')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
